// Workforce › Settings state (WS-A): portal settings (general, client redirect
// patterns, skill policy) and highlights (surface provider groups).
// Exported through ../index.ts already; add exports without touching that file.
//
// Portal settings themselves go through `usePortal(...).useUpdateMutator()` /
// `useDeleteMutator()` (consumer/loaders/portals.ts), which PATCH/DELETE /portals/:id.

import type {
  DashboardInstancePortalsListingsListQuery,
  DashboardInstancePortalsSurfaceProviderGroupsCreateBody,
  DashboardInstancePortalsSurfaceProviderGroupsUpdateBody
} from '@metorial/dashboard-sdk';
import { createLoader, useMutation } from '@metorial/data-hooks';
import { autoPaginate } from '../lib/autoPaginate';
import { withAuth } from '../user';

type PortalInput = { instanceId: string; portalId: string };

/**
 * Highlight groups ("provider groups" on the portal's consumer surface): the curated
 * sections the end-user portal shows on its home page and integrations sidebar.
 * All of them, oldest first, ordered by `index` on the client.
 */
export let portalHighlightGroupsLoader = createLoader({
  name: 'portalHighlightGroups',
  parents: [],
  fetch: (i: PortalInput) =>
    withAuth(sdk =>
      autoPaginate(cursor =>
        sdk.portals.providerGroups.list(i.instanceId, i.portalId, {
          ...cursor,
          order: 'asc'
        })
      )
    ).then(items => [...items].sort((a, b) => a.index - b.index)),
  mutators: {
    create: (
      body: DashboardInstancePortalsSurfaceProviderGroupsCreateBody,
      { input }: { input: PortalInput }
    ) =>
      withAuth(sdk => sdk.portals.providerGroups.create(input.instanceId, input.portalId, body)),

    update: (
      body: DashboardInstancePortalsSurfaceProviderGroupsUpdateBody & { groupId: string },
      { input }: { input: PortalInput }
    ) =>
      withAuth(sdk =>
        sdk.portals.providerGroups.update(input.instanceId, input.portalId, body.groupId, {
          name: body.name,
          description: body.description,
          index: body.index
        })
      ),

    delete: ({ groupId }: { groupId: string }, { input }: { input: PortalInput }) =>
      withAuth(sdk => sdk.portals.providerGroups.delete(input.instanceId, input.portalId, groupId))
  }
});

/**
 * Every access listing of the portal (integrations, Magic MCP servers, skills, ...),
 * each with the highlight groups it belongs to (`groups`).
 */
export let portalHighlightListingsLoader = createLoader({
  name: 'portalHighlightListings',
  parents: [],
  fetch: (
    i: PortalInput &
      Omit<DashboardInstancePortalsListingsListQuery, 'after' | 'before' | 'cursor' | 'limit'>
  ) =>
    withAuth(sdk =>
      autoPaginate(cursor =>
        sdk.portals.consumerAccessListings.list(i.instanceId, i.portalId, {
          ...i,
          ...cursor
        })
      )
    ),
  mutators: {}
});

export let usePortalHighlights = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let input = instanceId && portalId ? { instanceId, portalId } : null;
  let groups = portalHighlightGroupsLoader.use(input);
  let listings = portalHighlightListingsLoader.use(
    input ? { ...input, type: ['provider_template', 'magic_mcp_server'] } : null
  );

  return {
    groups: {
      ...groups,
      useCreateMutator: groups.useMutator('create'),
      useUpdateMutator: groups.useMutator('update'),
      useDeleteMutator: groups.useMutator('delete')
    },
    listings
  };
};

let refetchHighlights = () => {
  portalHighlightGroupsLoader.refetchAll();
  portalHighlightListingsLoader.refetchAll();
};

/** Adds an access listing to a highlight group. */
export let useAddPortalHighlightListing = () =>
  useMutation(
    (i: PortalInput & { groupId: string; consumerAccessListingId: string }) =>
      withAuth(sdk =>
        sdk.portals.providerGroups.addListing(i.instanceId, i.portalId, i.groupId, {
          consumerAccessListingId: i.consumerAccessListingId
        })
      ),
    { onSuccess: refetchHighlights }
  );

/** Removes an access listing from a highlight group. */
export let useRemovePortalHighlightListing = () =>
  useMutation(
    (i: PortalInput & { groupId: string; consumerAccessListingId: string }) =>
      withAuth(sdk =>
        sdk.portals.providerGroups.removeListing(
          i.instanceId,
          i.portalId,
          i.groupId,
          i.consumerAccessListingId
        )
      ),
    { onSuccess: refetchHighlights }
  );
